import paper from "paper";
import jsPDF from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import type { Project } from "../model/Project";
import type { StampLibrary } from "../stamps/StampLibrary";
import { projectToSvg } from "./sceneToSvg";
import { renderNotesPage } from "./notesPage";
import {
  findScaleCheckSpot,
  scaleCheckCaptionAnchor,
  chooseScaleBandRows,
  rectHitsPoly,
  SCALE_CHECK_CLEARANCE_MM,
  type Rect,
  type Poly,
} from "./scaleCheckPlacement";
import { extractSteps, type Step } from "../instructions/stepExtractor";
import {
  instructionStepHeight,
  layoutInstructionSteps,
} from "./instructionsLayout";
import { isFeatureEnabled } from "../featureFlags";
import { triggerDownload } from "./download";
import { fetchAccountId } from "./exportMeta";
import { getUnits } from "../preferences";

export type PageSizeId =
  | "a5"
  | "a4"
  | "a3"
  | "a2"
  | "a1"
  | "a0"
  | "letter"
  | "legal"
  | "tabloid"
  | "ansi-c"
  | "ansi-d"
  | "ansi-e"
  | "arch-c"
  | "arch-d"
  | "arch-e"
  | "arch-e1";
export type PageOrientation = "auto" | "portrait" | "landscape";
export type RegistrationMark = "none" | "crosshair" | "bullseye" | "scissors";

/** Clearance steps (mm) tried when placing the on-pattern scale-check squares
 *  (#577): the full 1/2" first, then progressively tighter, down to flush. A box
 *  touching the pattern beats one printed over it, so we only resort to an
 *  overlapping corner when not even a zero-gap slot exists. */
const SCALE_CHECK_CLEARANCE_STEPS = [
  SCALE_CHECK_CLEARANCE_MM,
  9.5,
  6.35,
  3,
  0,
] as const;

/** Test-only hook: when `__captureScaleCheck` is set, the exporter records the
 *  chosen scale-check box and the obstacle rects it cleared (page mm) so an e2e
 *  can verify the box doesn't sit on real geometry (#577). */
type ScaleCheckProbe = typeof globalThis & {
  __captureScaleCheck?: boolean;
  __scaleCheckDebug?: {
    box: Rect;
    obstacles: Poly[];
    clearance: number;
    overlapping: boolean;
    /** The host tile held no pattern — this is a standalone boxes-only page. */
    boxesOnly: boolean;
  };
};

export interface PdfOptions {
  pageSize?: PageSizeId;
  /** Manual orientation; "auto" (the default) keeps the size's natural
   *  portrait layout. Advanced printing (#289) exposes portrait / landscape. */
  orientation?: PageOrientation;
  /** Margin in mm. */
  margin?: number;
  /** The tiled pattern pages themselves (default true). Lets the cover /
   *  instructions be printed on their own — e.g. in small format after the
   *  pattern was plotted large (advanced printing, #289). */
  includePattern?: boolean;
  includeInstructions?: boolean;
  /** A page listing every note's full text, numbered and grouped by the part it
   *  points at, printed before the instructions (default true) — the print
   *  dialog's Content toggle (#157). Independent of `includeNotesOnPattern`:
   *  the words can be read off a page without the cards covering the geometry,
   *  or marked on the pattern without a page, or both. */
  includeNotesPage?: boolean;
  /** Draw the note cards and their callout lines on the pattern tiles, where
   *  the maker placed them (default true) — the print dialog's Content toggle
   *  (#157). */
  includeNotesOnPattern?: boolean;
  /** Draw the construction lines on the pattern pages (default true) —
   *  the print dialog's Content toggle (#870). */
  includeConstructionLines?: boolean;
  /** Draw the point markers (and, where the `point-letters` flag allows, their
   *  letters) that say which hole meets which (default true) — the print
   *  dialog's Content toggle (#693). Turning it off also drops the matching
   *  "Line up the parts to sew" instruction step, so the sheet and the steps
   *  never disagree about what is on the pattern. */
  includePointMarkers?: boolean;
  /** Title / scale-check cover page (default true). Skipped for blueprint-size
   *  printing where a separate cover sheet is unwanted (advanced printing,
   *  #289). */
  includeCoverPage?: boolean;
  /** How the tile layout treats paper. "both" (default) lays tiles out inside
   *  the area common to A4 *and* Letter so one PDF prints 1:1 on either; "exact"
   *  uses the chosen page's full printable area (advanced printing, #289). */
  fitPaper?: "both" | "exact";
  /** Tiled "DrawLeather.com" watermark behind every page (default true).
   *  Advanced printing turns it off (#289). */
  watermark?: boolean;
  /** Corner trim marks on each pattern tile (advanced printing, #289). */
  cropMarks?: boolean;
  /** Edge registration marks straddling the cut line between adjacent tiles,
   *  labelled with the sheet to join to (advanced printing, #289). */
  registrationMark?: RegistrationMark;
  /** Signed-in account id stamped in the footer, or null for an anonymous /
   *  local export (issue #289). `undefined` means "resolve it from `/api/me`"
   *  — the `downloadPdf` / `printPdf` wrappers fill this in. */
  exportedBy?: string | null;
  /** Epoch ms the export was produced; defaults to now. */
  exportedAt?: number;
}

/** Paper sizes in their natural portrait orientation (mm). Landscape swaps
 *  width / height at layout time. ISO A series up to A0, plus the ANSI/US
 *  series up to ANSI E (~A0). */
const PAGE_SIZES_MM: Record<PageSizeId, { w: number; h: number }> = {
  a5: { w: 148, h: 210 },
  a4: { w: 210, h: 297 },
  a3: { w: 297, h: 420 },
  a2: { w: 420, h: 594 },
  a1: { w: 594, h: 841 },
  a0: { w: 841, h: 1189 },
  letter: { w: 215.9, h: 279.4 },
  legal: { w: 215.9, h: 355.6 },
  tabloid: { w: 279.4, h: 431.8 },
  "ansi-c": { w: 431.8, h: 558.8 },
  "ansi-d": { w: 558.8, h: 863.6 },
  "ansi-e": { w: 863.6, h: 1117.6 },
  "arch-c": { w: 457.2, h: 609.6 },
  "arch-d": { w: 609.6, h: 914.4 },
  "arch-e": { w: 914.4, h: 1219.2 },
  "arch-e1": { w: 762, h: 1066.8 },
};

/** Whether a sheet is larger than Tabloid (11×17). On bigger paper the title /
 *  scale-check cover and the step-by-step instructions are usually unwanted
 *  (large-format / blueprint printing wants just the pattern), so both default
 *  off there (#289). */
const TABLOID_LONG_MM = PAGE_SIZES_MM.tabloid.h; // 431.8 mm

export function isAboveTabloid(pageSize: PageSizeId): boolean {
  const p = PAGE_SIZES_MM[pageSize];
  return Math.max(p.w, p.h) > TABLOID_LONG_MM;
}

export async function exportPdf(
  project: Project,
  library: StampLibrary,
  options: PdfOptions = {},
): Promise<Blob> {
  const pageSize = resolvePageSize(options);
  const margin = options.margin ?? 10;
  const includePattern = options.includePattern ?? true;
  const includeInstructions = options.includeInstructions ?? true;
  const includeCoverPage = options.includeCoverPage ?? true;
  const includeNotesPage = options.includeNotesPage ?? true;
  const base = PAGE_SIZES_MM[pageSize];

  // Where the scale-check squares live, in priority (#577): the cover page when
  // it's printed; otherwise the instructions page when it's printed (it has room
  // to spare); otherwise on the pattern tiles themselves.
  const instructionsHostScaleCheck = !includeCoverPage && includeInstructions;
  const patternHostsScaleCheck =
    !includeCoverPage && !includeInstructions && includePattern;

  // Pattern geometry, computed once up front — it both drives automatic
  // orientation (#585) and is rendered below. Print export draws every line in
  // black; the red/blue CAM colors only matter for the layered SVG.
  const pattern = includePattern
    ? projectToSvg(project, library, {
        monochrome: true,
        markOffPitch: isFeatureEnabled("stitch-matching"),
        pointMarkers: options.includePointMarkers !== false,
        pointLetters: isFeatureEnabled("point-letters"),
        includeConstructionLines: options.includeConstructionLines,
        includeNotes: options.includeNotesOnPattern !== false,
      })
    : null;

  // Orientation: explicit portrait/landscape, or "auto" (the default in advanced
  // settings, #585) which lays the pattern out both ways and keeps the one that
  // needs fewer sheets.
  const orientation = resolveOrientation(
    options,
    base,
    margin,
    pattern,
    patternHostsScaleCheck,
  );
  // Landscape swaps the natural portrait dimensions; all layout math below
  // reads `page`, so it follows the chosen orientation.
  const page = orientation === "landscape" ? { w: base.h, h: base.w } : base;

  // Pass explicit portrait dimensions rather than a named format so every
  // size here works regardless of jsPDF's built-in format list; `orientation`
  // still drives the landscape swap.
  const doc = new jsPDF({
    unit: "mm",
    format: [base.w, base.h],
    orientation,
  });

  // Tracks whether the document's initial blank page 1 has been claimed yet, so
  // whichever enabled section comes first uses it and the rest add pages — no
  // blank leading page when an earlier section is skipped (#289).
  const started = { used: false };

  // Cover page (#289): title + print-scale calibration boxes, watermark behind.
  if (includeCoverPage) {
    nextPage(doc, started);
    drawWatermark(doc, page, options);
    // The scale-check squares only make sense when the pattern they'd verify is
    // in the same PDF.
    renderCoverPage(doc, project, page, margin, includePattern);
  }

  // The pattern tiles. When omitted, the page-number footer line still needs a
  // y; fall back to the bottom of the printable area.
  let pageNumberY: number;
  if (pattern) {
    pageNumberY = await renderTiledPattern(
      doc,
      pattern.svg,
      pattern.bounds,
      page,
      margin,
      options,
      started,
      pattern.obstacles,
      patternHostsScaleCheck,
    );
  } else {
    const safe = safeArea(page, margin, options);
    pageNumberY = safe.y + safe.h - 6;
  }

  // The notes page goes BEFORE the instructions (#157): it is what the maker
  // wrote about this pattern, and it should be read before following anybody
  // else's steps. Renders nothing when there are no notes, so an empty section
  // never costs a blank sheet.
  if (includeNotesPage) {
    renderNotesPage(doc, project, safeArea(page, margin, options), () => {
      nextPage(doc, started);
      drawWatermark(doc, page, options);
    });
  }

  if (includeInstructions) {
    // Instructions need a paper scope to compute on-piece stitch-hole counts
    // (#910): a seam that overflows its piece marks only the holes that land on
    // the leather. Use a throwaway offscreen scope (same pattern as the SVG
    // exporter) and tear it down after; the step count is read synchronously.
    const stepScope = new paper.PaperScope();
    const stepCanvas = document.createElement("canvas");
    stepCanvas.width = 1;
    stepCanvas.height = 1;
    stepScope.setup(stepCanvas);
    const steps = extractSteps(project, library, stepScope, {
      pointMarkers: options.includePointMarkers !== false,
    });
    stepScope.project.remove();
    // The "join the sheets" guide only belongs in the instructions when
    // registration marks were actually printed AND the pattern spans more than
    // one sheet (so joining is needed) (#588).
    const multiSheet =
      pattern !== null &&
      planPatternTiles(
        pattern.bounds,
        page,
        margin,
        options,
        pattern.obstacles,
        patternHostsScaleCheck,
      ).gridTotal > 1;
    const showRegistrationGuide =
      multiSheet &&
      options.registrationMark !== undefined &&
      options.registrationMark !== "none";
    await renderInstructionsSection(
      doc,
      steps,
      page,
      margin,
      project,
      options,
      started,
      instructionsHostScaleCheck,
      showRegistrationGuide,
    );
  }

  // Footer page numbers, stamped last so the total is known. Every page,
  // including the cover and instructions.
  stampPageNumbers(doc, page, margin, pageNumberY, options);

  // Branding + export-identity footer on every page (issue #289): the
  // "Designed using DrawLeather.com" line plus who exported it and when.
  stampFooterBranding(doc, page, margin, options);

  return doc.output("blob");
}

/**
 * Cover page. The pattern title sits at the top. When `scaleCheck` is set the
 * bottom half also carries two reference squares — 3 cm and 2 in — so the user
 * can measure a printout and confirm it came out at 100 % (no printer scaling).
 * Each square is annotated with engineering dimension lines carrying the
 * measurement. The squares are omitted when the pattern itself isn't in this
 * PDF, since a scale check the pattern wasn't printed against would mislead
 * (different printers scale differently).
 */
function renderCoverPage(
  doc: jsPDF,
  project: Project,
  page: { w: number; h: number },
  margin: number,
  scaleCheck: boolean,
) {
  // Title sits one third of the way down the page, shifting the whole block
  // toward the bottom.
  const titleY = page.h / 3;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(20);
  doc.text(project.name || "Leather Pattern", page.w / 2, titleY, {
    align: "center",
  });

  if (!scaleCheck) return;

  // Scale-check note, padded below the title.
  const noteY = titleY + 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90);
  doc.text(SCALE_CHECK_NOTE, page.w / 2, noteY, { align: "center" });

  // Calibration squares, centred, padded below the note (plus 1 in of extra
  // breathing room). Full layout (height dimensions too) since the cover has
  // room to spare.
  const { w: blockW } = scaleSquaresSize(false);
  drawScaleSquares(doc, (page.w - blockW) / 2, noteY + 12 + 25.4, false);
}

/** Printer-calibration instruction shown beside the scale-check squares. They
 *  verify the *printer* isn't scaling, so the wording works wherever the squares
 *  appear — cover, instructions, or on the pattern itself. */
const SCALE_CHECK_NOTE =
  "Print at 100% (Actual size — no scaling). Measure the boxes to verify.";

const MM_PER_IN = 25.4;
/** Reference squares: 3 cm and 2 in, so either a metric or imperial ruler
 *  confirms a 100% printout. */
const SCALE_SQUARES = [
  { size: 30, label: "3 cm" },
  { size: 2 * MM_PER_IN, label: "2 in" },
] as const;

/** Footprint of the scale-check squares block drawn by {@link drawScaleSquares}.
 *  `compact` drops the right-hand height dimensions and tightens the gap so the
 *  block fits an empty corner of a pattern tile (#577); the full layout keeps
 *  both dimensions for the roomy cover page. */
function scaleSquaresSize(compact: boolean): { w: number; h: number } {
  const dimOffset = 8;
  const gap = compact ? 12 : 30;
  const tallest = Math.max(SCALE_SQUARES[0].size, SCALE_SQUARES[1].size);
  // Width: both squares + gap, plus the last square's right-hand height
  // dimension when not compact.
  const w =
    SCALE_SQUARES[0].size +
    SCALE_SQUARES[1].size +
    gap +
    (compact ? 0 : dimOffset + 4);
  // Height: tallest square + its width dimension line and label below.
  const h = tallest + dimOffset + 6;
  return { w, h };
}

/** Draw the two calibration squares with engineering dimension lines, top-left
 *  anchored at (x, y). Shared by the cover, the instructions size-check step,
 *  and the on-pattern placement. Returns the block's footprint. */
function drawScaleSquares(
  doc: jsPDF,
  x: number,
  y: number,
  compact: boolean,
): { w: number; h: number } {
  const dimOffset = 8;
  const gap = compact ? 12 : 30;
  doc.setDrawColor(0);
  doc.setFillColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(14);
  doc.setTextColor(20);
  let sx = x;
  for (const sq of SCALE_SQUARES) {
    // Diagonal shading inside the square (light grey so it reads as fill, not
    // a cut line), then the black outline on top.
    doc.setDrawColor(170);
    hatchSquare(doc, sx, y, sq.size, 2.5);
    doc.setDrawColor(0);
    doc.rect(sx, y, sq.size, sq.size);
    // Width — horizontal dimension line below the square.
    hDimLine(
      doc,
      sx,
      sx + sq.size,
      y + sq.size,
      y + sq.size + dimOffset,
      sq.label,
    );
    // Height — vertical dimension line to the right of the square. Skipped in
    // the compact layout to save horizontal room.
    if (!compact) {
      vDimLine(
        doc,
        y,
        y + sq.size,
        sx + sq.size,
        sx + sq.size + dimOffset,
        sq.label,
      );
    }
    sx += sq.size + gap;
  }
  return scaleSquaresSize(compact);
}

/** The instructions' size-check line (#578): a flowing sentence with a small
 *  hatched-square glyph (the height of the text) standing in for each reference
 *  box — "Measure ▫ 3 cm (metric) or ▫ 2 in (imperial) boxes to verify the
 *  scale." Unlike
 *  the cover / pattern the squares are *not* drawn at actual size here; they just
 *  point at the boxes the maker should measure. Returns the baseline y. */
function drawScaleCheckReferences(
  doc: jsPDF,
  x: number,
  y: number,
  fontSize: number,
): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(60);
  // Glyph as tall as the text's cap height.
  const icon = (fontSize * 25.4) / 72 / 1.1;
  const baseline = y + icon;
  const spaceW = doc.getTextWidth(" ");
  // "box" tokens render as the small hatched glyph; strings render as text.
  const segments: Array<string | "box"> = [
    "Measure",
    "box",
    "3 cm (metric) or",
    "box",
    "2 in (imperial) boxes to verify the scale.",
  ];
  let ix = x;
  segments.forEach((seg, i) => {
    if (seg === "box") {
      // An extra space-width of breathing room to the left of each glyph.
      ix += spaceW;
      doc.setLineWidth(0.2);
      doc.setDrawColor(150);
      hatchSquare(doc, ix, baseline - icon, icon, 1);
      doc.setDrawColor(0);
      doc.rect(ix, baseline - icon, icon, icon);
      ix += icon;
    } else {
      doc.text(seg, ix, baseline, { baseline: "alphabetic" });
      ix += doc.getTextWidth(seg);
    }
    if (i < segments.length - 1) ix += spaceW;
  });
  return baseline;
}

/** Fill an axis-aligned square with 45° hatch lines, each analytically clipped
 *  to the square. `spacing` is the perpendicular-ish step between lines (mm
 *  along the x+y axis). */
function hatchSquare(
  doc: jsPDF,
  x0: number,
  y0: number,
  size: number,
  spacing: number,
) {
  const x1 = x0 + size;
  const y1 = y0 + size;
  // Lines of the form x + y = c sweeping across the square.
  for (let c = x0 + y0 + spacing; c < x1 + y1; c += spacing) {
    const pts: Array<[number, number]> = [];
    const atX0 = c - x0;
    if (atX0 >= y0 && atX0 <= y1) pts.push([x0, atX0]);
    const atX1 = c - x1;
    if (atX1 >= y0 && atX1 <= y1) pts.push([x1, atX1]);
    const atY0 = c - y0;
    if (atY0 >= x0 && atY0 <= x1) pts.push([atY0, y0]);
    const atY1 = c - y1;
    if (atY1 >= x0 && atY1 <= x1) pts.push([atY1, y1]);
    // A line through two opposite corners satisfies all four edge tests and
    // yields duplicate points; dedupe so we connect the two *distinct* ends
    // rather than drawing a zero-length segment (which dropped the diagonal
    // whenever size was an exact multiple of spacing).
    const uniq = pts.filter(
      (p, i) =>
        pts.findIndex(
          (q) => Math.abs(q[0] - p[0]) < 1e-6 && Math.abs(q[1] - p[1]) < 1e-6,
        ) === i,
    );
    if (uniq.length >= 2) {
      const a = uniq[0]!;
      const b = uniq[1]!;
      doc.line(a[0], a[1], b[0], b[1]);
    }
  }
}

/** Filled arrowhead. Tip at (tipX, tipY); (dirX, dirY) is the unit vector
 *  pointing from the tip back along the dimension line. */
function arrowHead(
  doc: jsPDF,
  tipX: number,
  tipY: number,
  dirX: number,
  dirY: number,
) {
  // 3:1 aspect ratio — three times as long as it is wide.
  const width = 1.2;
  const len = width * 3;
  const half = width / 2;
  const baseX = tipX + dirX * len;
  const baseY = tipY + dirY * len;
  const perpX = -dirY;
  const perpY = dirX;
  doc.triangle(
    tipX,
    tipY,
    baseX + perpX * half,
    baseY + perpY * half,
    baseX - perpX * half,
    baseY - perpY * half,
    "F",
  );
}

/** Horizontal dimension line from xL to xR at yLine, with extension lines up to
 *  the measured edge (yEdge) and inward-pointing arrowheads. The label is
 *  horizontal and centred on the line; the line is broken to make room for it
 *  (unidirectional dimensioning). */
function hDimLine(
  doc: jsPDF,
  xL: number,
  xR: number,
  yEdge: number,
  yLine: number,
  label: string,
) {
  doc.line(xL, yEdge, xL, yLine + 1.5);
  doc.line(xR, yEdge, xR, yLine + 1.5);
  const mid = (xL + xR) / 2;
  const halfGap = doc.getTextWidth(label) / 2 + 1.5;
  if (mid - halfGap > xL) doc.line(xL, yLine, mid - halfGap, yLine);
  if (mid + halfGap < xR) doc.line(mid + halfGap, yLine, xR, yLine);
  arrowHead(doc, xL, yLine, 1, 0);
  arrowHead(doc, xR, yLine, -1, 0);
  doc.text(label, mid, yLine, { align: "center", baseline: "middle" });
}

/** Vertical dimension line from yT to yB at xLine, with extension lines out to
 *  the measured edge (xEdge) and inward-pointing arrowheads. The label is
 *  horizontal (not rotated) and centred on the line; the line is broken to make
 *  room for it (unidirectional dimensioning). */
function vDimLine(
  doc: jsPDF,
  yT: number,
  yB: number,
  xEdge: number,
  xLine: number,
  label: string,
) {
  doc.line(xEdge, yT, xLine + 1.5, yT);
  doc.line(xEdge, yB, xLine + 1.5, yB);
  const mid = (yT + yB) / 2;
  // Gap sized to the label's height (font em in mm) so the line clears the
  // horizontal text running across it.
  const halfGap = (doc.getFontSize() * 25.4) / 72 / 2 + 1;
  if (mid - halfGap > yT) doc.line(xLine, yT, xLine, mid - halfGap);
  if (mid + halfGap < yB) doc.line(xLine, mid + halfGap, xLine, yB);
  arrowHead(doc, xLine, yT, 0, 1);
  arrowHead(doc, xLine, yB, 0, -1);
  doc.text(label, xLine, mid, { align: "center", baseline: "middle" });
}

/** Advance to a fresh page, or claim the document's initial blank page 1 the
 *  first time it's called. Lets sections be skipped without leaving a blank
 *  leading page (#289). */
function nextPage(doc: jsPDF, started: { used: boolean }) {
  if (started.used) doc.addPage();
  else started.used = true;
}

interface TilePlanRow {
  tileY: number;
  slotH: number;
  pageY: number;
  band: "none" | "top" | "bottom";
}

interface TilePlan {
  safe: Rect;
  safeX: number;
  usableW: number;
  scaleBandH: number;
  bandPosition: "top" | "bottom" | "none";
  renderRows: TilePlanRow[];
  rows: number;
  cols: number;
  hostRow: number;
  /** "r,c" → sequential assembly-sheet number (only tiles with pattern). */
  tileNumber: Map<string, number>;
  /** Number of pattern (assembly grid) sheets. */
  gridTotal: number;
  /** Whether the scale-check host tile has no pattern and prints as a standalone
   *  "check the print size" page (no border / marks / grid cell). */
  boxesOnlyHost: boolean;
  /** Total printed pages including any boxes-only host — what auto orientation
   *  compares (#585). */
  pageCount: number;
  footerY: number;
  footerMidY: number;
  cellW: number;
  cellH: number;
}

/** Compute the tiling layout for a pattern on a given page (pure — no drawing):
 *  the row plan (including any reserved scale-check band), the printed-sheet
 *  numbering with empty tiles dropped, and the footer geometry. Shared by the
 *  renderer and by automatic orientation detection (#585), which runs this for
 *  both orientations and keeps the one with fewer sheets (`total`). */
function planPatternTiles(
  bounds: { x: number; y: number; w: number; h: number },
  page: { w: number; h: number },
  margin: number,
  options: PdfOptions,
  obstacles: Poly[],
  placeScaleCheckOnPattern: boolean,
): TilePlan {
  // The scale-check placement only runs when the pattern hosts it; emptiness
  // detection always uses the obstacles.
  const scaleCheckObstacles: Poly[] | null = placeScaleCheckOnPattern
    ? obstacles
    : null;
  // Lay the pattern out inside the printable area — common to BOTH A4 and Letter
  // by default so a tile prints in full on either paper at 100% scale, or the
  // chosen page's full printable area under advanced "exact" fit. The diagram
  // area is 20mm shorter than the full printable height, re-centred vertically.
  const safe = safeArea(page, margin, options);
  const safeX = safe.x;
  const usableW = safe.w;
  const fullUsableH = safe.h - 20;
  // Centre the shorter diagram area, then nudge it up 10mm to trim top padding.
  const fullSafeY = (page.h - fullUsableH) / 2 - 10;

  // If the scale-check squares can't find any clear slot in the pattern, reserve
  // a band for them and shift the pattern off it — rather than printing the
  // squares over the pattern (#577). Decide this up front (with the un-shifted
  // layout) so the tiling accounts for the lost height. Guard: keep a sensible
  // minimum pattern area, else accept the overlapping corner.
  let scaleBandH = 0;
  if (scaleCheckObstacles) {
    const { w: blockW, h: blockH } = scaleCheckBlockSize();
    const content = { x: safeX, y: fullSafeY, w: usableW, h: fullUsableH };
    const polys = mapObstaclesToPage(scaleCheckObstacles, content, {
      tileX: bounds.x,
      tileY: bounds.y,
    });
    const spot = scaleCheckSpotGraduated(content, blockW, blockH, polys);
    const band = blockH + 8;
    if (!spot && fullUsableH - band >= 50) scaleBandH = band;
  }

  // Row plan. Normally uniform full-height rows, re-centred. In the band case the
  // band sits above *or* below the pattern — whichever splits fewer pieces across
  // page boundaries (same page count either way) — so one row is shorter and the
  // pattern is drawn off the reserved band.
  let bandPosition: "top" | "bottom" | "none" = "none";
  let renderRows: TilePlanRow[];
  if (scaleBandH > 0 && scaleCheckObstacles) {
    const chosen = chooseScaleBandRows(
      bounds.y,
      bounds.h,
      fullUsableH,
      scaleBandH,
      scaleCheckObstacles,
    );
    bandPosition = chosen.position;
    renderRows = chosen.rows.map((row) => ({
      tileY: row.tileY,
      slotH: row.slotH,
      // Band above → draw the pattern below the band; otherwise pin it to the top
      // and (band below) the band falls under it.
      pageY: row.band === "top" ? safe.y + scaleBandH : safe.y,
      band: row.band,
    }));
  } else {
    const rowCount = Math.max(1, Math.ceil(bounds.h / fullUsableH));
    renderRows = Array.from({ length: rowCount }, (_, r) => ({
      tileY: bounds.y + r * fullUsableH,
      slotH: fullUsableH,
      pageY: fullSafeY,
      band: "none" as const,
    }));
  }
  const rows = renderRows.length;
  const cols = Math.max(1, Math.ceil(bounds.w / usableW));
  // The tile that carries the squares: first row normally / for a top band, last
  // row for a bottom band.
  const hostRow = bandPosition === "bottom" ? rows - 1 : 0;

  // Drop tiles with no pattern geometry — a reserved band (or a sparse pattern)
  // can leave a grid cell empty, which would otherwise print as a blank page and
  // show as an empty cell in the sheet map. A tile is printed when geometry falls
  // in its region, or it hosts the scale-check squares (#577).
  const tileGeom = (r: number, c: number) => {
    const row = renderRows[r]!;
    const tileX = bounds.x + c * usableW;
    const tileY = row.tileY;
    return {
      tileX,
      tileY,
      tileW: Math.min(usableW, bounds.x + bounds.w - tileX),
      tileH: Math.min(row.slotH, bounds.y + bounds.h - tileY),
    };
  };
  const hasGeometry = (g: ReturnType<typeof tileGeom>) => {
    if (g.tileW <= 0 || g.tileH <= 0) return false;
    if (obstacles.length === 0) return true; // nothing to test → keep the page
    const rect = { x: g.tileX, y: g.tileY, w: g.tileW, h: g.tileH };
    return obstacles.some((poly) => rectHitsPoly(rect, poly));
  };
  // Number only the tiles that carry pattern — those are the assembly sheets
  // (grid diagram, "Sheet X of Y", border, registration marks). The scale-check
  // host tile that has *no* pattern (a band-only last sheet) is printed too, but
  // it's just a "check the print size" page: kept out of the grid, but still
  // counted in the page total the auto-orientation chooser compares (#585).
  const tileNumber = new Map<string, number>();
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (hasGeometry(tileGeom(r, c))) {
        tileNumber.set(`${r},${c}`, tileNumber.size + 1);
      }
    }
  }
  const gridTotal = tileNumber.size;
  // The host sheet is "boxes only" when the scale-check rides on the pattern but
  // its host tile holds no geometry.
  const boxesOnlyHost =
    placeScaleCheckOnPattern && !tileNumber.has(`${hostRow},0`);
  const pageCount = gridTotal + (boxesOnlyHost ? 1 : 0);

  // Footer band geometry (constant for the whole document). The whole pattern +
  // band region ends `fullUsableH` below its top regardless of the band side.
  const regionTop = scaleBandH > 0 ? safe.y : fullSafeY;
  const footerY = regionTop + fullUsableH + 4;
  const cellH = Math.min(5, 15 / rows);
  const cellW = cellH * (usableW / fullUsableH);
  const footerMidY = footerY + (rows * cellH) / 2;

  return {
    safe,
    safeX,
    usableW,
    scaleBandH,
    bandPosition,
    renderRows,
    rows,
    cols,
    hostRow,
    tileNumber,
    gridTotal,
    boxesOnlyHost,
    pageCount,
    footerY,
    footerMidY,
    cellW,
    cellH,
  };
}

async function renderTiledPattern(
  doc: jsPDF,
  svg: string,
  bounds: { x: number; y: number; w: number; h: number },
  page: { w: number; h: number },
  margin: number,
  options: PdfOptions,
  started: { used: boolean },
  /** Every printed item's outline polygon (pattern space) — used to drop empty
   *  tiles and, when `placeScaleCheckOnPattern`, to place the scale-check. */
  obstacles: Poly[],
  /** Whether the scale-check squares ride on the pattern (no cover / instructions
   *  page to host them, #577). */
  placeScaleCheckOnPattern: boolean,
): Promise<number> {
  const scaleCheckObstacles: Poly[] | null = placeScaleCheckOnPattern
    ? obstacles
    : null;
  const {
    safe,
    safeX,
    usableW,
    bandPosition,
    renderRows,
    rows,
    cols,
    hostRow,
    tileNumber,
    gridTotal,
    boxesOnlyHost,
    footerY,
    footerMidY,
    cellW,
    cellH,
  } = planPatternTiles(
    bounds,
    page,
    margin,
    options,
    obstacles,
    placeScaleCheckOnPattern,
  );
  const sheetAt = (r: number, c: number) => tileNumber.get(`${r},${c}`);

  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml");
  const svgEl = parsed.documentElement as unknown as SVGSVGElement;

  for (let r = 0; r < rows; r += 1) {
    const row = renderRows[r]!;
    const pageY = row.pageY;
    for (let c = 0; c < cols; c += 1) {
      const sheetNum = sheetAt(r, c);
      const isHostTile = placeScaleCheckOnPattern && r === hostRow && c === 0;
      const boxesOnly = isHostTile && boxesOnlyHost;
      // Empty tiles that aren't the boxes-only host get no page.
      if (sheetNum === undefined && !boxesOnly) continue;

      nextPage(doc, started);
      drawWatermark(doc, page, options);

      const tileX = bounds.x + c * usableW;
      const tileY = row.tileY;
      const tileW = Math.min(usableW, bounds.x + bounds.w - tileX);
      const tileH = Math.min(row.slotH, bounds.y + bounds.h - tileY);

      // The pattern itself (skipped on the boxes-only page — there's none).
      if (!boxesOnly && tileW > 0 && tileH > 0) {
        svgEl.setAttribute("viewBox", `${tileX} ${tileY} ${tileW} ${tileH}`);
        svgEl.setAttribute("width", `${tileW}mm`);
        svgEl.setAttribute("height", `${tileH}mm`);

        // svg2pdf wants a live SVG element attached to the DOM.
        const host = document.createElement("div");
        host.style.position = "absolute";
        host.style.left = "-99999px";
        host.appendChild(svgEl.cloneNode(true) as Node);
        document.body.appendChild(host);
        try {
          await svg2pdf(host.firstChild as SVGElement, doc, {
            x: safeX,
            y: pageY,
            width: tileW,
            height: tileH,
          });
        } finally {
          document.body.removeChild(host);
        }
      }

      // Border, crop / registration marks, and the sheet footer belong to the
      // pattern assembly grid — skipped on the boxes-only "check the print size"
      // page, which carries no pattern to cut or align (#577).
      if (!boxesOnly) {
        // Dashed border around the printable pattern slot — if it prints whole,
        // nothing in the pattern was clipped. This same rectangle is the cut
        // line the crop / registration marks reference.
        drawSafeAreaBorder(doc, safeX, pageY, usableW, row.slotH);

        // Advanced printing marks (#289): corner trim marks, and registration
        // marks straddling the cut line shared with each neighbouring tile —
        // only toward neighbours that are themselves pattern sheets.
        if (options.cropMarks) {
          drawCropMarks(doc, safeX, pageY, usableW, row.slotH);
        }
        if (options.registrationMark && options.registrationMark !== "none") {
          drawRegistrationMarks(
            doc,
            options.registrationMark,
            { x: safeX, y: pageY, w: usableW, h: row.slotH },
            c,
            r,
            sheetAt,
          );
        }

        // Footer: sheet number, grid dimensions, and a mini sheet-map locator.
        // Only shown when more than one assembly sheet is printed.
        if (gridTotal > 1) {
          doc.setFontSize(8);
          doc.setTextColor(120);
          const label = `Sheet ${sheetNum} of ${gridTotal}`;
          // The "(cols x rows grid)" line only makes sense when every cell is a
          // real sheet; if any were dropped (empty / boxes-only) the grid has
          // holes, so omit it and just label the sheet.
          const fullGrid = gridTotal === cols * rows;
          const dims = `(${cols} x ${rows} grid)`;
          const labelW = fullGrid
            ? Math.max(doc.getTextWidth(label), doc.getTextWidth(dims))
            : doc.getTextWidth(label);
          const gridX = safeX + labelW + 6;
          if (fullGrid) {
            doc.text(label, safeX, footerMidY - 1, { baseline: "bottom" });
            doc.text(dims, safeX, footerMidY + 1, { baseline: "top" });
          } else {
            doc.text(label, safeX, footerMidY, { baseline: "middle" });
          }
          drawTileMap(
            doc,
            gridX,
            footerY,
            cols,
            rows,
            sheetAt,
            c,
            r,
            cellW,
            cellH,
          );
        }
      }

      // Scale-check squares ride on the pattern when there's no cover page or
      // instructions to host them (#577). They go on the host tile, in blank
      // space at least 1/2" clear of all geometry. When the pattern leaves no
      // gap, the reserved band (above or below the pattern) holds them instead;
      // if the host tile holds no pattern at all this page shows only the boxes.
      if (scaleCheckObstacles && isHostTile) {
        const forced =
          bandPosition === "top"
            ? { x: safeX + 3, y: safe.y + 3 }
            : bandPosition === "bottom"
              ? { x: safeX + 3, y: pageY + row.slotH + 3 }
              : undefined;
        placeScaleCheckOnTile(
          doc,
          { x: safeX, y: pageY, w: usableW, h: row.slotH },
          { tileX, tileY },
          scaleCheckObstacles,
          forced,
          boxesOnly,
        );
      }
    }
  }

  // The page number (stamped later, on every page) sits on the tile label's
  // line so it stays within the printable area.
  return footerMidY - 1;
}

/** Draw the scale-check squares onto a pattern tile (#577). Finds blank space in
 *  the tile's printable area that clears every printed obstacle by at least
 *  1/2" — preferring the top-left corner, then the top-right, then the first
 *  free slot scanning top-to-bottom — so the squares never sit on the pattern.
 *  If nothing fits (the geometry fills the sheet), the top-left corner is used
 *  as a last resort. A faint caption labels the block.
 *
 *  `content` is the printable area in page (mm) coordinates; `origin` is the
 *  pattern-space coordinate drawn at the content's top-left, so obstacles (in
 *  pattern space) can be mapped onto the page. */
const SCALE_CHECK_CAP_H = 6; // caption line above the squares

/** Footprint of the on-pattern scale-check block (compact squares + caption). */
function scaleCheckBlockSize(): { w: number; h: number } {
  const s = scaleSquaresSize(true);
  return { w: s.w, h: s.h + SCALE_CHECK_CAP_H };
}

/** Map pattern-space obstacle polygons to page coordinates (svg2pdf draws the
 *  tile 1:1 with viewBox origin (tileX,tileY) at the content's top-left). */
function mapObstaclesToPage(
  obstacles: Poly[],
  content: Rect,
  origin: { tileX: number; tileY: number },
): Poly[] {
  return obstacles.map((poly) =>
    poly.map(
      ([ox, oy]) =>
        [content.x + (ox - origin.tileX), content.y + (oy - origin.tileY)] as [
          number,
          number,
        ],
    ),
  );
}

/** Find a slot for the block, stepping the clearance down from the full 1/2"
 *  before giving up — a box that merely touches the pattern beats one over it.
 *  Returns the spot and the clearance it used, or null when nothing fits. */
function scaleCheckSpotGraduated(
  content: Rect,
  blockW: number,
  blockH: number,
  pageObstacles: Poly[],
): { x: number; y: number; clearance: number } | null {
  for (const clr of SCALE_CHECK_CLEARANCE_STEPS) {
    const spot = findScaleCheckSpot(content, blockW, blockH, pageObstacles, {
      clearance: clr,
    });
    if (spot) return { ...spot, clearance: clr };
  }
  return null;
}

/** Draw the scale-check block (caption + compact squares) at top-left (x,y). */
function drawScaleCheckAt(doc: jsPDF, content: Rect, x: number, y: number) {
  const { w: blockW } = scaleCheckBlockSize();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  // Anchor the caption so it follows the block's side and never overflows the
  // printable border (right-aligned when the block sits on the right).
  const captionW = doc.getTextWidth(SCALE_CHECK_NOTE);
  const caption = scaleCheckCaptionAnchor(x, blockW, content, captionW);
  doc.text(SCALE_CHECK_NOTE, caption.x, y + 4, {
    align: caption.align,
    baseline: "alphabetic",
  });
  drawScaleSquares(doc, x, y + SCALE_CHECK_CAP_H, true);
}

/** Place the scale-check squares on a tile. With `forced` set (the reserved-band
 *  case, where the pattern was shifted down to make room) the block goes exactly
 *  there; otherwise it searches for a clear slot and, only if the pattern leaves
 *  no gap at all, drops into the top-left corner. */
function placeScaleCheckOnTile(
  doc: jsPDF,
  content: Rect,
  origin: { tileX: number; tileY: number },
  obstacles: Poly[],
  forced?: { x: number; y: number },
  boxesOnly = false,
) {
  const { w: blockW, h: blockH } = scaleCheckBlockSize();
  const pageObstacles = mapObstaclesToPage(obstacles, content, origin);

  let place: { x: number; y: number };
  let clearance: number;
  let overlapping: boolean;
  if (forced) {
    // The band was reserved above the (shifted-down) pattern, so it's clear.
    place = forced;
    clearance = -1;
    overlapping = false;
  } else {
    const found = scaleCheckSpotGraduated(
      content,
      blockW,
      blockH,
      pageObstacles,
    );
    place = found ?? { x: content.x + 3, y: content.y + 3 };
    clearance = found?.clearance ?? -1;
    overlapping = found === null;
  }

  // Test hook (#577): expose the real placement + mapped obstacles so an e2e can
  // assert the box clears the actual geometry. No-op in normal use.
  if ((globalThis as ScaleCheckProbe).__captureScaleCheck) {
    (globalThis as ScaleCheckProbe).__scaleCheckDebug = {
      box: { x: place.x, y: place.y, w: blockW, h: blockH },
      obstacles: pageObstacles,
      clearance,
      overlapping,
      boxesOnly,
    };
  }

  drawScaleCheckAt(doc, content, place.x, place.y);
}

/** Mini sheet-map locator: a grid of small rectangles mirroring the tile layout,
 *  with the current tile (curCol, curRow) filled black and the rest outlined.
 *  Each printed cell carries its sheet number; `numberAt` returns undefined for
 *  empty (un-printed) cells, which are skipped so the map shows only real sheets
 *  (#577). */
function drawTileMap(
  doc: jsPDF,
  x: number,
  y: number,
  cols: number,
  rows: number,
  numberAt: (r: number, c: number) => number | undefined,
  curCol: number,
  curRow: number,
  cellW: number,
  cellH: number,
) {
  doc.setLineWidth(0.15);
  // Number height ≈ 45% of the cell, capped so it never looks oversized.
  doc.setFontSize(Math.min(6, ((cellH * 0.45) / 25.4) * 72));
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const num = numberAt(r, c);
      if (num === undefined) continue; // empty cell — leave a gap
      const cx = x + c * cellW;
      const cy = y + r * cellH;
      const current = r === curRow && c === curCol;
      if (current) {
        doc.setFillColor(0, 0, 0);
        doc.rect(cx, cy, cellW, cellH, "F");
        doc.setTextColor(255, 255, 255);
      } else {
        doc.setDrawColor(120);
        doc.rect(cx, cy, cellW, cellH, "S");
        doc.setTextColor(40, 40, 40);
      }
      doc.text(String(num), cx + cellW / 2, cy + cellH / 2, {
        align: "center",
        baseline: "middle",
      });
    }
  }
  doc.setDrawColor(0);
  doc.setTextColor(20);
}

/** Stamp "Page N of M" on the right of every page (cover, tiles, and
 *  instructions), aligned to the tile footer line (`y`) so it stays within the
 *  printable area on both A4 and Letter. Run after all pages exist. */
function stampPageNumbers(
  doc: jsPDF,
  page: { w: number; h: number },
  margin: number,
  y: number,
  options: PdfOptions,
) {
  const safe = safeArea(page, margin, options);
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Page ${i} of ${total}`, safe.x + safe.w, y, {
      align: "right",
      baseline: "bottom",
    });
  }
}

/** Printable area inset by `margin` and centred on the page. By default it's
 *  the area common to A4 *and* Letter, so a tile prints in full on either paper
 *  at 100% scale; under advanced "exact" fit (#289) it's the chosen page's own
 *  printable area (which honours larger sizes and landscape). */
function safeArea(
  page: { w: number; h: number },
  margin: number,
  options?: PdfOptions,
): { x: number; y: number; w: number; h: number } {
  if (options?.fitPaper === "exact") {
    return {
      x: margin,
      y: margin,
      w: page.w - margin * 2,
      h: page.h - margin * 2,
    };
  }
  const commonW = Math.min(PAGE_SIZES_MM.a4.w, PAGE_SIZES_MM.letter.w);
  const commonH = Math.min(PAGE_SIZES_MM.a4.h, PAGE_SIZES_MM.letter.h);
  const w = commonW - margin * 2;
  const h = commonH - margin * 2;
  return { x: (page.w - w) / 2, y: (page.h - h) / 2, w, h };
}

/** Dashed rectangle marking the common A4/Letter printable area. Drawn in grey
 *  so it reads as a guide rather than part of the (black) pattern; resets the
 *  dash pattern and draw colour afterwards. */
function drawSafeAreaBorder(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  doc.setDrawColor(120);
  doc.setLineWidth(0.2);
  doc.setLineDashPattern([2, 2], 0);
  doc.rect(x, y, w, h);
  doc.setLineDashPattern([], 0);
  doc.setDrawColor(0);
}

/** Corner trim (crop) marks just outside each corner of the cut box (#289).
 *  Two short ticks per corner with a small gap, the printer's convention for
 *  "trim here", so a sheet can be cut down to the printable area precisely. */
function drawCropMarks(doc: jsPDF, x: number, y: number, w: number, h: number) {
  const len = 4; // tick length (mm)
  const gap = 1.5; // clear space between the corner and the tick
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  const corners = [
    { cx: x, cy: y, sx: -1, sy: -1 },
    { cx: x + w, cy: y, sx: 1, sy: -1 },
    { cx: x, cy: y + h, sx: -1, sy: 1 },
    { cx: x + w, cy: y + h, sx: 1, sy: 1 },
  ];
  for (const { cx, cy, sx, sy } of corners) {
    // Horizontal tick running outward, and a vertical tick running outward.
    doc.line(cx + sx * gap, cy, cx + sx * (gap + len), cy);
    doc.line(cx, cy + sy * gap, cx, cy + sy * (gap + len));
  }
}

/** Registration marks on the cut box edges shared with neighbouring tiles
 *  (#289). Each mark is centred ON the cut line so a scissors cut bisects it;
 *  the matching half prints on the adjoining sheet, and a small "join sheet N"
 *  label names which tile that edge connects to. Marks are only drawn on edges
 *  that actually have a neighbour. */
function drawRegistrationMarks(
  doc: jsPDF,
  kind: RegistrationMark,
  safe: { x: number; y: number; w: number; h: number },
  col: number,
  row: number,
  /** Neighbour's assembly-sheet number, or undefined when it isn't a pattern
   *  sheet (empty / a boxes-only page) — no mark is drawn toward it (#577). */
  sheetAt: (r: number, c: number) => number | undefined,
) {
  const drawMark = (mx: number, my: number, neighbor: number, edge: Edge) => {
    drawRegIcon(doc, kind, mx, my, edge);
    drawJoinLabel(doc, mx, my, neighbor, edge);
  };
  const midX = safe.x + safe.w / 2;
  const midY = safe.y + safe.h / 2;
  const right = sheetAt(row, col + 1);
  if (right !== undefined) drawMark(safe.x + safe.w, midY, right, "right");
  const left = sheetAt(row, col - 1);
  if (left !== undefined) drawMark(safe.x, midY, left, "left");
  const bottom = sheetAt(row + 1, col);
  if (bottom !== undefined) drawMark(midX, safe.y + safe.h, bottom, "bottom");
  const top = sheetAt(row - 1, col);
  if (top !== undefined) drawMark(midX, safe.y, top, "top");
}

type Edge = "left" | "right" | "top" | "bottom";

/** A registration icon centred at (mx, my). All three styles fit inside the
 *  same ~9mm box so they straddle the cut line identically. The scissors are
 *  oriented along the cut line — rotated 90° on the vertical (left / right)
 *  edges so the blades always run *with* the line, not across it. */
function drawRegIcon(
  doc: jsPDF,
  kind: RegistrationMark,
  mx: number,
  my: number,
  edge: Edge,
) {
  const s = 9;
  const r = s / 2;
  doc.setDrawColor(0);
  doc.setFillColor(0, 0, 0);
  doc.setLineWidth(0.25);
  if (kind === "crosshair") {
    doc.circle(mx, my, r * 0.6, "S");
    doc.line(mx - r, my, mx + r, my);
    doc.line(mx, my - r, mx, my + r);
  } else if (kind === "bullseye") {
    doc.circle(mx, my, r, "S");
    doc.circle(mx, my, r * 0.55, "S");
    doc.circle(mx, my, 0.5, "F");
  } else if (kind === "scissors") {
    // The cut line is vertical on the left / right edges, horizontal on the
    // top / bottom. Rotate the (horizontally-drawn) scissors 90° so they run
    // along it. `p` maps a local offset (dx along the line, dy across it) to a
    // page point, applying that rotation.
    const vertical = edge === "left" || edge === "right";
    const p = (dx: number, dy: number): [number, number] =>
      vertical ? [mx - dy, my + dx] : [mx + dx, my + dy];
    const loopR = r * 0.32;
    const loop1 = p(-r * 0.7, -r * 0.45);
    const loop2 = p(-r * 0.7, r * 0.45);
    doc.circle(loop1[0], loop1[1], loopR, "S");
    doc.circle(loop2[0], loop2[1], loopR, "S");
    // Blades cross from each loop through the pivot to the open tips. Pull the
    // loop end in by `loopR` along the line so the blade starts at the loop's
    // edge, not its centre.
    const b1 = p(-r * 0.7 + loopR, -r * 0.45);
    const b2 = p(-r * 0.7 + loopR, r * 0.45);
    const tip1 = p(r, r * 0.5);
    const tip2 = p(r, -r * 0.5);
    doc.line(b1[0], b1[1], tip1[0], tip1[1]);
    doc.line(b2[0], b2[1], tip2[0], tip2[1]);
    const pivot = p(r * 0.15, 0);
    doc.circle(pivot[0], pivot[1], 0.4, "F");
  }
}

/** Small "✄ N" style label naming the tile this edge joins to, placed just
 *  inside the cut box next to the mark so it stays on the printed sheet. */
function drawJoinLabel(
  doc: jsPDF,
  mx: number,
  my: number,
  neighbor: number,
  edge: Edge,
) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(90);
  const label = `join sheet ${neighbor}`;
  // Perpendicular distance from the cut line to the label baseline — clears
  // the registration mark (radius `s/2` = 4.5) with a hair of breathing room.
  const pad = 6;
  if (edge === "right" || edge === "left") {
    // Vertical cut line: run the label *along* it. jsPDF's `align: "center"`
    // mis-centres rotated text (it offsets along the page's x-axis by the text
    // width, not along the rotated baseline), so we place the start anchor
    // manually: offset along the reading direction by half the text width to
    // centre it on the mark, and `pad` to the inside to clear the mark.
    const w = doc.getTextWidth(label);
    if (edge === "right") {
      // angle 270 advances the text downward; start half a width above the
      // mark centre so the label ends up centred on it.
      doc.text(label, mx - pad, my - w / 2, { baseline: "middle", angle: 270 });
    } else {
      // angle 90 advances upward; start half a width below the mark centre.
      doc.text(label, mx + pad, my + w / 2, { baseline: "middle", angle: 90 });
    }
  } else if (edge === "bottom") {
    doc.text(label, mx, my - pad, { align: "center", baseline: "bottom" });
  } else {
    doc.text(label, mx, my + pad, { align: "center", baseline: "top" });
  }
  doc.setTextColor(20);
}

/** Light-grey "DrawLeather.com" wordmark tiled diagonally across the page.
 *  Called at the start of every page (cover, tiles, instructions) so the page
 *  content draws on top of it — marking the sheet as a DrawLeather export /
 *  print without obscuring the cut lines (issue #289). No-op when watermarking
 *  is turned off (advanced printing). Restores the text colour afterwards so
 *  the caller's content keeps its own colour. */
function drawWatermark(
  doc: jsPDF,
  page: { w: number; h: number },
  options: PdfOptions,
) {
  if (options.watermark === false) return;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(225);
  const text = "DrawLeather.com";
  // Diagonal lattice: rows offset so the marks stagger like a brick wall.
  const stepX = 75;
  const stepY = 55;
  let row = 0;
  for (let y = 10; y < page.h + stepY; y += stepY, row += 1) {
    const offset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = -stepX + offset; x < page.w + stepX; x += stepX) {
      doc.text(text, x, y, { angle: 30, baseline: "middle" });
    }
  }
  doc.setTextColor(20);
}

/** Two centred footer lines stamped on every page (issue #289): who produced
 *  the export and when, then the "Designed using DrawLeather.com" mark. Anchored
 *  to the bottom of the common A4/Letter printable area so both lines clear the
 *  tile-locator / page-number band that sits higher in the footer. */
function stampFooterBranding(
  doc: jsPDF,
  page: { w: number; h: number },
  margin: number,
  options: PdfOptions,
) {
  const safe = safeArea(page, margin, options);
  const cx = safe.x + safe.w / 2;
  const bottom = safe.y + safe.h;
  const total = doc.getNumberOfPages();
  const when = new Date(options.exportedAt ?? Date.now()).toLocaleString();
  const who =
    options.exportedBy != null && options.exportedBy !== ""
      ? `Exported by account ${options.exportedBy} · ${when}`
      : `Exported ${when} · not signed in`;
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(140);
    doc.text(who, cx, bottom - 3.5, { align: "center", baseline: "middle" });
    // "Designed using DrawLeather.com" — only the domain is a clickable link, with
    // a small external-link icon (#587).
    stampBrandingLink(doc, cx, bottom - 0.5);
  }
  doc.setTextColor(20);
}

/** Schematic for the "join the sheets" instructions (#588): the sheet cut along
 *  the dashed line (through its registration marks) is laid *on top* of its
 *  uncut neighbour. The shared cut line is dashed; everything else is plain
 *  straight edges, with the back sheet a lighter grey for depth. The mark uses
 *  the chosen `kind`. Returns the y just below the diagram. */
function drawSheetJoinGuide(
  doc: jsPDF,
  x: number,
  y: number,
  kind: RegistrationMark,
): number {
  const w = 32;
  const h = 22;
  const overlap = 10;
  const drop = 3;
  // The uncut neighbour sits behind, offset down-right (lighter grey for depth).
  const bx = x + w - overlap;
  const by = y + drop;
  doc.setDrawColor(150);
  doc.setLineWidth(0.3);
  doc.rect(bx, by, w, h, "S");

  // The cut sheet sits on top — white-filled to occlude the overlap, then its
  // three plain edges and the dashed cut line on the right.
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, w, h, "F");
  doc.setDrawColor(70);
  doc.setLineWidth(0.3);
  doc.line(x, y, x + w, y); // top
  doc.line(x, y + h, x + w, y + h); // bottom
  doc.line(x, y, x, y + h); // left
  // The cut line (right edge) is dashed — this is where you cut, regardless of
  // which registration icon sits on it. It continues past the cut sheet's edge
  // down onto the uncut sheet, to that sheet's bottom edge, showing where the cut
  // edge lands once overlaid.
  doc.setLineDashPattern([1.5, 1.5], 0);
  doc.line(x + w, y, x + w, by + h);
  doc.setLineDashPattern([], 0);
  // Registration mark straddling the cut line.
  drawRegIcon(doc, kind, x + w, y + h / 2, "right");
  doc.setDrawColor(0);

  // Captions on each sheet's visible part.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(90);
  doc.text("cut, lay on top", x + (w - overlap) / 2, y + h + 4, {
    align: "center",
    baseline: "top",
  });
  doc.text("uncut", (x + w + (bx + w)) / 2, by + h + 1.5, {
    align: "center",
    baseline: "top",
  });
  doc.setTextColor(20);
  return Math.max(y + h, by + h) + 9;
}

/** The DrawLeather URL the footer link points at (#587). */
const DRAWLEATHER_URL = "https://drawleather.com";

/** Draw "Designed using DrawLeather.com" centred at `cx` (middle baseline at
 *  `y`), with only "DrawLeather.com" + a trailing external-link glyph rendered as
 *  a clickable link (#587). */
function stampBrandingLink(doc: jsPDF, cx: number, y: number) {
  const prefix = "Designed using ";
  const linkText = "DrawLeather.com";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  const textH = (doc.getFontSize() * 25.4) / 72; // em height in mm
  const prefixW = doc.getTextWidth(prefix);
  const linkW = doc.getTextWidth(linkText);
  const iconGap = 0.8;
  const iconSize = textH * 0.7;
  const totalW = prefixW + linkW + iconGap + iconSize;
  const startX = cx - totalW / 2;

  // Prefix in the muted branding grey.
  doc.setTextColor(110);
  doc.text(prefix, startX, y, { baseline: "middle" });

  // Linked domain + icon in link blue.
  const linkX = startX + prefixW;
  doc.setTextColor(20, 90, 200);
  doc.text(linkText, linkX, y, { baseline: "middle" });
  const iconX = linkX + linkW + iconGap;
  drawExternalLinkIcon(doc, iconX, y - iconSize / 2, iconSize);

  // One clickable rectangle over the domain and the icon.
  doc.link(linkX, y - textH / 2, linkW + iconGap + iconSize, textH, {
    url: DRAWLEATHER_URL,
  });
  doc.setTextColor(20);
}

/** Small "open box + arrow" external-link glyph, top-left at (x, y), drawn in the
 *  current draw colour (set to match the link text). */
function drawExternalLinkIcon(doc: jsPDF, x: number, y: number, s: number) {
  doc.setDrawColor(20, 90, 200);
  doc.setLineWidth(0.18);
  // Box (lower-left), with its top-right corner left open for the arrow.
  doc.line(x, y + 0.35 * s, x, y + s); // left
  doc.line(x, y + s, x + 0.65 * s, y + s); // bottom
  doc.line(x + 0.65 * s, y + s, x + 0.65 * s, y + 0.55 * s); // right (partial)
  doc.line(x, y + 0.35 * s, x + 0.3 * s, y + 0.35 * s); // top (partial)
  // Arrow leaving the box toward the top-right.
  doc.line(x + 0.4 * s, y + 0.6 * s, x + s, y); // shaft
  doc.line(x + s, y, x + 0.55 * s, y); // arrowhead (top)
  doc.line(x + s, y, x + s, y + 0.45 * s); // arrowhead (side)
  doc.setDrawColor(0);
}

/** Render one wrapped line of step text, drawing any `links` phrases that fall
 *  on it as real blue, underlined, clickable text (each glyph drawn once — no
 *  black-then-blue overlay). The body text colour (20) is restored on return. */
function drawLineWithLinks(
  doc: jsPDF,
  line: string,
  x: number,
  baseline: number,
  links: { phrase: string; url: string }[],
  lineH: number,
): void {
  // jsPDF's getTextWidth ignores trailing spaces, so the gap before a link would
  // collapse. Measure a lone space reliably (difference of two widths) and add it
  // back when advancing past a prefix that ends in spaces.
  const spaceW = doc.getTextWidth("n n") - doc.getTextWidth("nn");
  let cursor = 0;
  let drawX = x;
  while (cursor < line.length) {
    // Earliest link phrase at/after the cursor on this line.
    let next: { idx: number; phrase: string; url: string } | null = null;
    for (const { phrase, url } of links) {
      const idx = line.indexOf(phrase, cursor);
      if (idx >= 0 && (next === null || idx < next.idx))
        next = { idx, phrase, url };
    }
    if (next === null) {
      doc.text(line.slice(cursor), drawX, baseline);
      break;
    }
    const before = line.slice(cursor, next.idx);
    if (before) {
      doc.text(before, drawX, baseline);
      const trimmed = before.replace(/ +$/, "");
      drawX +=
        doc.getTextWidth(trimmed) + (before.length - trimmed.length) * spaceW;
    }
    const w = doc.getTextWidth(next.phrase);
    doc.setTextColor(0, 102, 204); // link blue
    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(0.2);
    doc.text(next.phrase, drawX, baseline);
    doc.line(drawX, baseline + 0.7, drawX + w, baseline + 0.7); // underline
    doc.link(drawX, baseline - lineH * 0.75, w, lineH, { url: next.url });
    doc.setTextColor(20);
    doc.setDrawColor(20);
    drawX += w;
    cursor = next.idx + next.phrase.length;
  }
}

async function renderInstructionsSection(
  doc: jsPDF,
  steps: Step[],
  page: { w: number; h: number },
  margin: number,
  project: Project,
  options: PdfOptions,
  started: { used: boolean },
  /** When the instructions are the scale-check's home (no cover page, #577),
   *  print the real measuring squares here; otherwise just point at the squares
   *  printed elsewhere with the small inline glyphs (#578). */
  scaleCheckHere: boolean,
  /** Add the "cut out and join the sheets" guide — only when registration marks
   *  were printed and the pattern spans multiple sheets (#588). */
  showRegistrationGuide: boolean,
) {
  // Same printable area as the pattern tiles (so instructions match the chosen
  // fit), but without the dashed border.
  const safe = safeArea(page, margin, options);
  const bottom = safe.y + safe.h;

  nextPage(doc, started);
  drawWatermark(doc, page, options);
  doc.setFontSize(18);
  doc.setTextColor(20);
  doc.text(project.name || "Leather Pattern", safe.x, safe.y + 8);
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`${steps.length} steps`, safe.x, safe.y + 14);

  let y = safe.y + 24;

  // First, before any build step, the size-matching check: the maker has to
  // confirm the printout came out at 100% (#578). Numbered "0" so the build
  // steps keep their natural 1..N.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(20);
  doc.text("Before you start — check the print size", safe.x, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60);
  doc.text("Print at 100% (actual size — no scaling).", safe.x, y);
  y += 5;
  if (scaleCheckHere) {
    // The instructions are the scale-check's home: print the real 3 cm / 2 in
    // measuring squares here so the maker measures them directly (#577).
    doc.text(
      "Measure these boxes — each must match the dimension shown on it:",
      safe.x,
      y,
    );
    y += 6;
    // Full layout (width *and* height dimension lines), same as the cover page.
    const blk = drawScaleSquares(doc, safe.x, y, false);
    y += blk.h + 8;
  } else {
    // The squares are printed elsewhere (cover / pattern); just point at them
    // with the small inline glyphs (#578).
    y = drawScaleCheckReferences(doc, safe.x, y, 10) + 8;
  }

  // After the size check but before the numbered steps: how to cut out and join
  // the tiled sheets using the registration marks (#588). Only when marks were
  // printed and the pattern actually spans more than one sheet.
  if (showRegistrationGuide) {
    const kind: RegistrationMark =
      options.registrationMark && options.registrationMark !== "none"
        ? options.registrationMark
        : "crosshair";
    // Keep the whole section together: break to a new page if it won't fit.
    if (y > bottom - 64) {
      doc.addPage();
      drawWatermark(doc, page, options);
      y = safe.y + 8;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text("Cut out and join the sheets", safe.x, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60);
    const intro = doc.splitTextToSize(
      "Your pattern is tiled across several sheets. Join two neighbours by lining up the registration marks printed on their shared cut line:",
      safe.w,
    );
    doc.text(intro, safe.x, y);
    y += intro.length * 5 + 0.5;
    y = drawSheetJoinGuide(doc, safe.x, y, kind) + 3;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60);
    const how = doc.splitTextToSize(
      'Cut one of the two sheets along the dashed line running through its half-marks, then lay that cut sheet on top of its uncut neighbour so each mark\'s two halves line up exactly, and tape. The "join sheet N" label on every mark names the sheet that edge connects to.',
      safe.w,
    );
    doc.text(how, safe.x, y);
    y += how.length * 5 + 8;
  }

  doc.setFontSize(11);
  doc.setTextColor(20);

  // Plan the page breaks up front so no step spills past the footer — the old
  // check only reserved a fixed ~2 lines before each title, which overflowed on
  // the shorter landscape page (#712). Measure each step's wrapped body (in the
  // body font), then lay the steps out keeping each one whole.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const stepLines: string[][] = steps.map((s) =>
    doc.splitTextToSize(s.description, safe.w),
  );
  const layout = layoutInstructionSteps(
    steps.map((s, i) =>
      instructionStepHeight(stepLines[i]!.length, s.tools.length > 0),
    ),
    { top: y, bottom, pageTop: safe.y + 8 },
  );
  const lineH = doc.getLineHeight() / doc.internal.scaleFactor; // mm

  let curPage = 0;
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i]!;
    const place = layout[i]!;
    if (place.page > curPage) {
      doc.addPage();
      drawWatermark(doc, page, options);
      curPage = place.page;
    }
    y = place.y;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text(`${i + 1}. ${step.title}`, safe.x, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    const lines = stepLines[i]!;
    if (step.links?.length) {
      // Draw line-by-line so link phrases (#687: "skipping the stitch") render
      // as real blue, underlined, clickable text — each glyph drawn once.
      for (let k = 0; k < lines.length; k += 1) {
        drawLineWithLinks(
          doc,
          lines[k]!,
          safe.x,
          y + k * lineH,
          step.links,
          lineH,
        );
      }
    } else {
      doc.text(lines, safe.x, y);
    }
    y += lines.length * 5 + 4;
    if (step.tools.length) {
      doc.setTextColor(100);
      doc.text(`Tools: ${step.tools.join(", ")}`, safe.x, y);
      doc.setTextColor(20);
      y += 6;
    }
    y += 4;
  }
}

export async function downloadPdf(
  project: Project,
  library: StampLibrary,
  options: PdfOptions = {},
) {
  const opts = await withExportInfo(options);
  const blob = await exportPdf(project, library, opts);
  const sizeLabel = resolvePageSize(opts).toUpperCase();
  await triggerDownload(blob, `${sanitize(project.name)}_${sizeLabel}.pdf`);
}

/** Build the same print-ready PDF as the export, then hand it to the browser's
 *  print dialog via a hidden iframe (issue #289) — keeps the user on the page
 *  rather than bouncing through a download. */
export async function printPdf(
  project: Project,
  library: StampLibrary,
  options: PdfOptions = {},
) {
  const opts = await withExportInfo(options);
  const blob = await exportPdf(project, library, opts);
  await printBlob(blob);
}

/** Print a PDF blob through an off-screen iframe. Resolves once printing has
 *  been triggered; the iframe and its object URL are torn down after the print
 *  dialog closes (`afterprint`), with a generous timeout fallback for browsers
 *  that never fire it. */
function printBlob(blob: Blob): Promise<void> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
      iframe.remove();
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        resolve();
        return;
      }
      // Tear down once the user dismisses the dialog; the timeout covers
      // browsers that never deliver `afterprint`.
      win.addEventListener("afterprint", () => {
        cleanup();
      });
      setTimeout(cleanup, 60_000);
      try {
        win.focus();
        win.print();
      } catch {
        cleanup();
      }
      resolve();
    };

    iframe.src = url;
    document.body.appendChild(iframe);
  });
}

/** Fill in the export-identity options from `/api/me` when the caller hasn't
 *  supplied them. Leaves an explicit `exportedBy` (including `null`) untouched
 *  so tests and advanced callers can pin it. */
async function withExportInfo(options: PdfOptions): Promise<PdfOptions> {
  const exportedBy =
    options.exportedBy !== undefined
      ? options.exportedBy
      : await fetchAccountId();
  return {
    ...options,
    exportedBy,
    exportedAt: options.exportedAt ?? Date.now(),
  };
}

/** Paper size for an export: an explicit option wins, otherwise it follows the
 *  active unit system — Letter for imperial, A4 for metric. */
function resolvePageSize(options: PdfOptions): PageSizeId {
  return options.pageSize ?? (getUnits() === "in" ? "letter" : "a4");
}

/** Orientation for an export. Explicit "portrait"/"landscape" win; "auto" (the
 *  default, #585) tiles the pattern both ways and keeps the orientation that
 *  needs fewer sheets — falling back to portrait when there's no pattern to
 *  measure or the two tie. */
function resolveOrientation(
  options: PdfOptions,
  base: { w: number; h: number },
  margin: number,
  pattern: {
    bounds: { x: number; y: number; w: number; h: number };
    obstacles: Poly[];
  } | null,
  patternHostsScaleCheck: boolean,
): "portrait" | "landscape" {
  if (options.orientation === "portrait") return "portrait";
  if (options.orientation === "landscape") return "landscape";
  // Auto: nothing to optimize without a pattern.
  if (!pattern) return "portrait";
  const sheets = (page: { w: number; h: number }) =>
    planPatternTiles(
      pattern.bounds,
      page,
      margin,
      options,
      pattern.obstacles,
      patternHostsScaleCheck,
    ).pageCount;
  const portrait = sheets({ w: base.w, h: base.h });
  const landscape = sheets({ w: base.h, h: base.w });
  return landscape < portrait ? "landscape" : "portrait";
}

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9_-]/gi, "_") || "leather-art";
}
